/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Hidden, breakpoints } from '@coursera/cds-core';
import type { ButtonName } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import HeroModule from 'bundles/cds-labs/components/Modules/HeroModule';
import { MODULE_LAYOUTS } from 'bundles/cds-labs/components/Modules/ModuleLayout';
import type { MerchandisingHeroModuleFieldsFragment } from 'bundles/front-page/__generated__/contentfulTypes';

type HeroModuleProps = React.ComponentProps<typeof HeroModule>;

type Props = {
  content?: MerchandisingHeroModuleFieldsFragment | null;
};

const styles = {
  container: css`
    ${breakpoints.down('xs')} {
      text-align: center;
    }
  `,

  heading: css`
    ${breakpoints.down('xs')} {
      text-align: center;
    }
  `,
  description: css`
    margin-bottom: var(--cds-spacing-200);
    white-space: pre-line;

    ${breakpoints.up('md')} {
      width: 80%;
    }
  `,
  button: css`
    text-decoration: none;

    ${breakpoints.down('xs')} {
      width: 100%;
    }
  `,
  secondaryButton: css`
    && {
      box-shadow: inset 0 0 0 1px var(--cds-color-white-0);
    }
  `,
};

function MerchandisingHeroModuleRenderer({ content }: Props) {
  const track = useTracker();

  if (!content) {
    return null;
  }

  const trackClick = (buttonName: ButtonName, linkName: string, linkTarget: string) => () => {
    track('click_button', {
      button: { name: buttonName, linkName, linkURL: linkTarget, linkType: 'internal' },
      pageSection: {
        sectionName: 'hero_banner',
      },
    });
  };

  const {
    headline,
    description,
    shortDescription,
    backgroundColor,
    ctaText,
    ctaTarget,
    secondaryCtaTarget,
    secondaryCtaText,
    image,
    imageTablet,
    imageMobile,
    eyebrowImage,
  } = content;

  const titleToShow = (titleCopy: string) => <div css={styles.heading}>{titleCopy}</div>;
  const descriptionToShow = (descriptionCopy?: string | null) => (
    <p css={styles.description}>{descriptionCopy ?? ''}</p>
  );

  const getHeroModuleProps = ({ isMobile, isTablet }: { isMobile?: boolean; isTablet?: boolean }): HeroModuleProps => {
    let imageUrl = image?.url;
    if (isMobile) {
      imageUrl = imageMobile?.url;
    } else if (isTablet) {
      imageUrl = imageTablet?.url ?? image?.url;
    }

    return {
      headline: titleToShow(headline ?? ''),
      description: descriptionToShow(isMobile && shortDescription ? shortDescription : description),
      image: {
        url: imageUrl,
        width: isMobile || isTablet ? 600 : 720,
      },
      eyebrow: {
        image: {
          url: eyebrowImage?.url,
          height: isMobile ? 12 : 16,
        },
      },
      backgroundColor,
      layout: MODULE_LAYOUTS.text6image6,
      showImagesAboveContentOnMobile: false,
      preloadImage: true,
      customPrimaryCta:
        ctaText && ctaTarget ? (
          <Button
            component="a"
            variant="secondary"
            href={ctaTarget}
            css={styles.button}
            onClick={trackClick('hero_primary_cta', ctaText, ctaTarget)}
          >
            {ctaText}
          </Button>
        ) : undefined,
      customSecondaryCta:
        secondaryCtaText && secondaryCtaTarget ? (
          <Button
            component="a"
            variant="primary"
            href={secondaryCtaTarget}
            css={[styles.button, styles.secondaryButton]}
            onClick={trackClick('hero_secondary_cta', secondaryCtaText, secondaryCtaTarget)}
          >
            {secondaryCtaText}
          </Button>
        ) : undefined,
    };
  };

  return (
    <div css={styles.container}>
      <Hidden smUp>
        <HeroModule {...getHeroModuleProps({ isMobile: true, isTablet: false })} />
      </Hidden>
      <Hidden mdUp xsDown>
        <HeroModule {...getHeroModuleProps({ isMobile: false, isTablet: true })} />
      </Hidden>
      <Hidden smDown>
        <HeroModule {...getHeroModuleProps({ isMobile: false, isTablet: false })} />
      </Hidden>
    </div>
  );
}

export default MerchandisingHeroModuleRenderer;
